import { Glass } from "@coach/ui";
import { FileText } from "lucide-react";
import type { NoteSummary } from "../../repos/notes.js";

/**
 * Card for a Note row in the unified Whiteboard overview grid. Mirrors the
 * WhiteboardCard footprint (square-ish aspect, badge in the header row,
 * name + meta below) but uses a text-preview surface instead of a canvas
 * thumbnail.
 */
export function NoteCard({ summary, onOpen }: { summary: NoteSummary; onOpen: () => void }) {
  const preview = summary.preview?.trim() ?? "";
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group text-left"
      aria-label={`Notiz ${summary.name} öffnen`}
    >
      <Glass
        level="floating"
        padding="md"
        className="flex flex-col gap-3 transition-shadow group-hover:shadow-glow"
      >
        {/* Preview surface — soft sky tint to differentiate from whiteboards
            without breaking the global palette. */}
        <div
          className="relative aspect-16/10 w-full overflow-hidden rounded-2xl border border-white/6"
          style={{
            background: "linear-gradient(160deg, rgba(56,189,248,0.10), rgba(15,23,30,0.95))",
          }}
        >
          <div className="absolute inset-0 flex flex-col gap-1.5 p-4">
            <FileText className="size-5 text-sky-300/80" aria-hidden />
            <p className="line-clamp-4 text-[12px] leading-snug text-white/75">
              {preview || <span className="italic text-white/35">Leere Notiz</span>}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-sky-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-sky-300">
              Notiz
            </span>
            <span className="text-body font-semibold leading-tight">{summary.name}</span>
          </div>
          <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
            {formatDate(summary.updatedAt)}
          </span>
        </div>
      </Glass>
    </button>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
}
